import { CellAlign, CellType, HeadCell } from '@dfl/mui-admin-layout';
import { FlexBox, LongText } from '@dfl/mui-react-common';
import { ReactLink } from '@dfl/react-security';
import { TransTypography } from 'components/TransTypography';
import { CampaignRowActions } from 'modules/campaigns/components/common/CampaignRowActions';
import { CampaignStatus } from 'modules/campaigns/components/common/CampaignStatus';
import { CAMPAIGN_STATUS_ENUM } from 'modules/campaigns/constants/common/status.enum';
import { CAMPAIGN_TYPE_ENUM } from 'modules/campaigns/constants/common/type.enum';
import { ICampaign } from 'modules/campaigns/interfaces';
import { campaignStatus, getCampaignTypeIcon } from 'modules/campaigns/utils';
import { PAYMENT_METHOD_GATEWAY } from 'modules/common/constants/payment-method.enums';
import { IPlanOrder } from 'modules/subscriptions/interfaces/IPlanOrder';
import { showPrice } from 'utils/math';
import { getSmallPaymentGatewayImage } from 'utils/payment-methods';
import { endOfDay, formatDistanceStrict, startOfDay } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import i18n from 'i18next';
import React from 'react';

const nameColumn: HeadCell<ICampaign> = {
  field: 'name',
  headerName: 'campaigns:fields.name',
  disablePadding: false,
  sortable: false,
  renderCell: (value: string, data: ICampaign) => {
    const campaignType = CAMPAIGN_TYPE_ENUM.itemsPromotion;
    const url = `/campaigns/${campaignType.toLowerCase()}/details/${data?._id as string}`;

    return (
      <ReactLink to={url} underline={'hover'} className={'flex items-center gap-1'} maxWidth={210}>
        {getCampaignTypeIcon(campaignType)}
        {value || '-'}
      </ReactLink>
    );
  },
};

const descriptionColumn: HeadCell<ICampaign> = {
  field: 'description',
  headerName: 'campaigns:fields.description',
  disablePadding: false,
  sortable: false,
  renderCell: (description: string) => <LongText text={description} lineClamp={4} />,
};

const startDateColumn: HeadCell<ICampaign> = {
  field: 'startTime',
  headerName: 'campaigns:fields.startTime',
  sortable: false,
  type: CellType.DATE,
};

const durationColumn: HeadCell<ICampaign> = {
  field: 'duration',
  headerName: 'campaigns:fields.duration',
  sortable: false,
  renderCell: (_: any, campaign: ICampaign) => {
    if (campaign?.startTime && campaign?.endTime) {
      const startTime = startOfDay(new Date(campaign.startTime));
      const endTime = endOfDay(new Date(campaign.endTime));
      return formatDistanceStrict(startTime, endTime, {
        locale: i18n.language.startsWith('es') ? esLocale : enLocale,
        unit: 'day',
        roundingMethod: 'ceil',
      });
    } else {
      return '-';
    }
  },
};

const dateColumn: HeadCell<ICampaign> = {
  field: 'createdAt',
  headerName: 'common:createdAt',
  type: CellType.DATE,
  sortable: false,
};

const priceColumn: HeadCell<ICampaign> = {
  field: 'priceDetails.finalPrice',
  headerName: 'campaigns:fields.price',
  disablePadding: false,
  sortable: false,
  renderCell: (value: number, data: ICampaign) =>
    isFinite(value) ? `${showPrice(value)} ${data?.payment?.currency || 'CUP'}` : '-',
};

const statusColumn: HeadCell<ICampaign> = {
  field: 'status',
  headerName: 'campaigns:fields.status',
  disablePadding: false,
  sortable: false,
  renderCell: (_: any, data: ICampaign) => {
    const status = campaignStatus(data);
    return <CampaignStatus value={status || CAMPAIGN_STATUS_ENUM.CREATED} />;
  },
};

const paymentGatewayColumn: HeadCell<IPlanOrder> = {
  field: 'payment.gateway',
  headerName: 'common:paymentGateway.title',
  disablePadding: false,
  sortable: false,
  renderCell: (gateway: PAYMENT_METHOD_GATEWAY) => {
    const value = Object.values(PAYMENT_METHOD_GATEWAY).find((el) => el === gateway);

    if (value) {
      return (
        <FlexBox gap={1} alignItems={'center'}>
          {getSmallPaymentGatewayImage(gateway)}
          <TransTypography message={`common:paymentGateway.${value}.title`} />
        </FlexBox>
      );
    }

    return '-';
  },
};

const actionsColumn: HeadCell<ICampaign> = {
  field: 'actions',
  sortable: false,
  width: 100,
  headerAlign: CellAlign.RIGHT,
  permissions: ['ADMIN'],
  headerName: 'common:actions',
  disablePadding: true,
  component: CampaignRowActions,
};

export const promotionCampaignsColumns: Array<HeadCell<any>> = [
  nameColumn,
  descriptionColumn,
  startDateColumn,
  durationColumn,
  priceColumn,
  statusColumn,
  paymentGatewayColumn,
  dateColumn,
  actionsColumn,
];
